import * as React from "react";
import type {
  DashboardPropertyFilters,
  DashboardPropertyVideoFilter,
  DashboardPropertyVisibilityFilter,
  Tag,
} from "../../../convex/types";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelectTags } from "@/components/ui/multi-select-tags";
import { MultiSelectListingAgents } from "@/components/ui/multi-select-listing-agents";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PROPERTY_STATUS_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  PROPERTY_VIDEO_OPTIONS,
  PROPERTY_VISIBILITY_OPTIONS,
  uniqueNonEmptyStrings,
  type PropertyListingAgentOption,
} from "./filters";

type AdvancedPropertyFiltersProps = {
  filters: DashboardPropertyFilters;
  tags: Tag[];
  listingAgents: PropertyListingAgentOption[] | undefined;
  onFiltersChange: (nextFilters: DashboardPropertyFilters) => void;
};

/**
 * Organizes advanced property filters into smaller, task-focused groups.
 */
export function AdvancedPropertyFilters({
  filters,
  tags,
  listingAgents,
  onFiltersChange,
}: AdvancedPropertyFiltersProps) {
  const sortedAgents = React.useMemo(() => {
    if (!listingAgents) {
      return [];
    }
    return [...listingAgents].sort((a, b) =>
      a.agentName.localeCompare(b.agentName),
    );
  }, [listingAgents]);

  const listingAgentSelectOptions = React.useMemo(() => {
    const selectedKeys = uniqueNonEmptyStrings(filters.listingAgentKeys);
    const extras: PropertyListingAgentOption[] = [];
    for (const key of selectedKeys) {
      const inList = sortedAgents.some((agent) => agent.agentId === key);
      if (!inList) {
        extras.push({
          agentId: key,
          agentName: key,
          propertyCount: 0,
        });
      }
    }
    return [...extras, ...sortedAgents];
  }, [sortedAgents, filters.listingAgentKeys]);

  let listingAgentsField: React.ReactNode;
  if (listingAgents === undefined) {
    listingAgentsField = (
      <p className="text-sm text-muted-foreground">Loading listing agents…</p>
    );
  } else if (listingAgentSelectOptions.length === 0) {
    listingAgentsField = (
      <p className="text-sm text-muted-foreground">
        No listing agents with properties yet.
      </p>
    );
  } else {
    listingAgentsField = (
      <MultiSelectListingAgents
        options={listingAgentSelectOptions}
        selectedAgentIds={filters.listingAgentKeys}
        onSelectionChange={(nextIds) =>
          onFiltersChange({
            ...filters,
            listingAgentKeys: uniqueNonEmptyStrings(nextIds),
          })
        }
        placeholder="Any listing agent"
        searchPlaceholder="Search agents by name"
        emptyText="No agents match that search. Try a different name."
      />
    );
  }

  return (
    <div className="space-y-7">
      <FilterSection
        title="Find matches"
        description="Start with the filters staff use most often."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <FilterField label="Tags">
            <MultiSelectTags
              tags={tags}
              selectedTagIds={filters.tagIds}
              onSelectionChange={(nextTagIds) =>
                onFiltersChange({
                  ...filters,
                  tagIds: nextTagIds,
                })
              }
              placeholder="Filter by tags"
              searchPlaceholder="Search tags"
              emptyText="No tags found. Try a different search."
            />
          </FilterField>

          <FilterField label="Status">
            <Select
              value={filters.status === "" ? "all" : filters.status}
              onValueChange={(nextStatus) =>
                onFiltersChange({
                  ...filters,
                  status: nextStatus === "all" ? "" : nextStatus,
                })
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                {PROPERTY_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>

          <FilterField label="Video">
            <Select
              value={filters.hasVideoThumbnail}
              onValueChange={(nextValue) =>
                onFiltersChange({
                  ...filters,
                  hasVideoThumbnail: nextValue as DashboardPropertyVideoFilter,
                })
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Any video status" />
              </SelectTrigger>
              <SelectContent>
                {PROPERTY_VIDEO_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>

          <FilterField label="Visibility">
            <Select
              value={filters.visibility}
              onValueChange={(nextValue) =>
                onFiltersChange({
                  ...filters,
                  visibility: nextValue as DashboardPropertyVisibilityFilter,
                })
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Any visibility" />
              </SelectTrigger>
              <SelectContent>
                {PROPERTY_VISIBILITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
        </div>

        <FilterField label="Listing agents">{listingAgentsField}</FilterField>
      </FilterSection>

      <FilterSection
        title="Property type"
        description="Include every property type you want to show."
      >
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {PROPERTY_TYPE_OPTIONS.map((option) => {
            const checked = filters.propertyTypes.includes(option.value);
            return (
              <label
                key={option.value}
                className="flex min-h-11 items-center gap-2 rounded-md border border-transparent px-3 py-2 text-sm transition-colors hover:bg-muted/50 focus-within:border-border focus-within:bg-muted/30"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() =>
                    onFiltersChange({
                      ...filters,
                      propertyTypes: toggleMultiValue(
                        filters.propertyTypes,
                        option.value,
                      ),
                    })
                  }
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      </FilterSection>

      <FilterSection
        title="Rooms and age"
        description="Set minimum and maximum values when you need them."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <RangeField
            label="Bedrooms"
            minValue={filters.bedroomMin}
            maxValue={filters.bedroomMax}
            minPlaceholder="Minimum bedrooms"
            maxPlaceholder="Maximum bedrooms"
            onMinChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                bedroomMin: nextValue,
              })
            }
            onMaxChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                bedroomMax: nextValue,
              })
            }
          />

          <RangeField
            label="Bathrooms"
            minValue={filters.bathroomMin}
            maxValue={filters.bathroomMax}
            minPlaceholder="Minimum bathrooms"
            maxPlaceholder="Maximum bathrooms"
            onMinChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                bathroomMin: nextValue,
              })
            }
            onMaxChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                bathroomMax: nextValue,
              })
            }
          />

          <RangeField
            label="Year built"
            minValue={filters.yearBuiltMin}
            maxValue={filters.yearBuiltMax}
            minPlaceholder="Built after"
            maxPlaceholder="Built before"
            onMinChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                yearBuiltMin: nextValue,
              })
            }
            onMaxChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                yearBuiltMax: nextValue,
              })
            }
          />
        </div>
      </FilterSection>

      <FilterSection
        title="Size and price"
        description="Use these when you need a tighter match."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <RangeField
            label="Build size (sqm)"
            minValue={filters.buildSizeMin}
            maxValue={filters.buildSizeMax}
            minPlaceholder="Minimum sqm"
            maxPlaceholder="Maximum sqm"
            onMinChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                buildSizeMin: nextValue,
              })
            }
            onMaxChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                buildSizeMax: nextValue,
              })
            }
          />

          <RangeField
            label="Land size (sqm)"
            minValue={filters.landSizeMin}
            maxValue={filters.landSizeMax}
            minPlaceholder="Minimum sqm"
            maxPlaceholder="Maximum sqm"
            onMinChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                landSizeMin: nextValue,
              })
            }
            onMaxChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                landSizeMax: nextValue,
              })
            }
          />

          <RangeField
            label="Total price"
            minValue={filters.priceMin}
            maxValue={filters.priceMax}
            minPlaceholder="Minimum price"
            maxPlaceholder="Maximum price"
            onMinChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                priceMin: nextValue,
              })
            }
            onMaxChange={(nextValue) =>
              onFiltersChange({
                ...filters,
                priceMax: nextValue,
              })
            }
          />
        </div>
      </FilterSection>
    </div>
  );
}

type FilterSectionProps = {
  title: string;
  description: string;
  children: React.ReactNode;
};

function FilterSection({ title, description, children }: FilterSectionProps) {
  return (
    <section className="space-y-3 border-t pt-6 first:border-t-0 first:pt-0">
      <div className="max-w-2xl space-y-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

type FilterFieldProps = {
  label: string;
  children: React.ReactNode;
};

function FilterField({ label, children }: FilterFieldProps) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      {children}
    </div>
  );
}

type RangeFieldProps = {
  label: string;
  minValue: number | null;
  maxValue: number | null;
  minPlaceholder: string;
  maxPlaceholder: string;
  onMinChange: (value: number | null) => void;
  onMaxChange: (value: number | null) => void;
};

function RangeField({
  label,
  minValue,
  maxValue,
  minPlaceholder,
  maxPlaceholder,
  onMinChange,
  onMaxChange,
}: RangeFieldProps) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        <Input
          aria-label={`${label} minimum`}
          inputMode="numeric"
          value={minValue === null ? "" : minValue.toString()}
          onChange={(event) =>
            onMinChange(parseNullableNumber(event.target.value))
          }
          placeholder={minPlaceholder}
          className="h-10"
        />
        <Input
          aria-label={`${label} maximum`}
          inputMode="numeric"
          value={maxValue === null ? "" : maxValue.toString()}
          onChange={(event) =>
            onMaxChange(parseNullableNumber(event.target.value))
          }
          placeholder={maxPlaceholder}
          className="h-10"
        />
      </div>
    </div>
  );
}

function toggleMultiValue(values: string[], nextValue: string): string[] {
  if (values.includes(nextValue)) {
    return values.filter((value) => value !== nextValue);
  }

  return [...values, nextValue];
}

function parseNullableNumber(value: string): number | null {
  const trimmedValue = value.trim();
  if (trimmedValue === "") {
    return null;
  }

  const parsedValue = Number(trimmedValue);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return parsedValue;
}
